// Safe localStorage wrapper for environments where localStorage may be unavailable
// (e.g., iOS Safari Private Browsing) or throws on access.

export const safeLocalStorage: Storage = (() => {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) {
      throw new Error('No window.localStorage');
    }
    const testKey = '__safe_storage_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return window.localStorage;
  } catch {
    const memoryStore: Record<string, string> = {};
    return {
      getItem: (key: string) => (key in memoryStore ? memoryStore[key] : null),
      setItem: (key: string, value: string) => {
        memoryStore[key] = value;
      },
      removeItem: (key: string) => {
        delete memoryStore[key];
      },
      clear: () => {
        for (const k in memoryStore) delete memoryStore[k];
      },
      key: (index: number) => Object.keys(memoryStore)[index] ?? null,
      get length() {
        return Object.keys(memoryStore).length;
      },
    } as Storage;
  }
})();

export const safeGetItem = (key: string): string | null => {
  try {
    return safeLocalStorage.getItem(key);
  } catch {
    return null;
  }
};

export const safeSetItem = (key: string, value: string): void => {
  try {
    safeLocalStorage.setItem(key, value);
  } catch {
    // no-op
  }
};

export const safeRemoveItem = (key: string): void => {
  try {
    safeLocalStorage.removeItem(key);
  } catch {
    // no-op
  }
};
